import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Copy, X, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export const FollowUpDialog = ({ open, onOpenChange, commitment }) => {
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState({ subject: "", body: "" });

  useEffect(() => {
    if (!open || !commitment) return;
    setLoading(true);
    api
      .followUp(commitment.id)
      .then((res) => setDraft({ subject: res.subject || "", body: res.body || "" }))
      .catch(() => toast.error("Failed to draft follow-up"))
      .finally(() => setLoading(false));
  }, [open, commitment]);

  const copy = async () => {
    const text = `Subject: ${draft.subject}\n\n${draft.body}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied draft to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="followup-dialog"
        className="max-w-2xl bg-ledger-surface border border-ledger-border p-0 gap-0"
      >
        <DialogHeader className="px-8 pt-7 pb-4 border-b border-ledger-border">
          <DialogTitle className="font-display text-2xl text-ledger-ink flex items-center gap-2">
            <Sparkles className="h-5 w-5" /> Draft follow-up
          </DialogTitle>
          <div className="text-sm text-ledger-sub mt-1">
            Context-aware reminder about the specific commitment &mdash; not a generic ping.
          </div>
        </DialogHeader>

        <div className="px-8 py-5 space-y-3">
          {loading ? (
            <div className="h-40 grid place-items-center scanning rounded-sm font-mono text-xs text-[#78350F]">
              Drafting…
            </div>
          ) : (
            <>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-ledger-muted mb-1">
                  Subject
                </div>
                <input
                  data-testid="followup-subject"
                  value={draft.subject}
                  onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
                  className="w-full bg-white border border-ledger-border rounded-sm px-3 py-2 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-ledger-ink"
                />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-ledger-muted mb-1">
                  Message
                </div>
                <textarea
                  data-testid="followup-body"
                  value={draft.body}
                  rows={10}
                  onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
                  className="w-full bg-white border border-ledger-border rounded-sm p-3 font-sans text-sm leading-6 focus:outline-none focus:ring-2 focus:ring-ledger-ink"
                />
              </div>
            </>
          )}
        </div>

        <div className="px-8 py-4 border-t border-ledger-border flex items-center justify-between">
          <button
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 text-sm text-ledger-sub hover:text-ledger-ink inline-flex items-center gap-1"
            data-testid="followup-close"
          >
            <X className="h-4 w-4" /> Close
          </button>
          <button
            onClick={copy}
            disabled={loading}
            data-testid="followup-copy"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ledger-ink text-white text-sm font-medium hover:bg-neutral-800 disabled:opacity-60 transition-colors duration-200"
          >
            <Copy className="h-4 w-4" /> Copy draft
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
